import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { ShieldCheck, Warning, ArrowRight, CheckCircle } from '@phosphor-icons/react';

/** Compliance-widget: toont welke facturen van de huidige maand nog niet via Peppol
 *  verzonden zijn (wettelijk verplicht sinds 1/1/2026 in België). */
export default function PeppolComplianceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard/peppol-compliance')
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || data.error) return null;
  if (data.klant_wel_peppol === 0 && data.te_versturen_count === 0) return null; // geen Peppol-klanten → widget verbergen

  const score = data.compliance_score_pct || 0;
  const score_kleur = score >= 95 ? '#059669' : score >= 70 ? '#D97706' : '#DC2626';
  const score_bg = score >= 95 ? '#ECFDF5' : score >= 70 ? '#FFFBEB' : '#FEF2F2';
  const score_border = score >= 95 ? '#A7F3D0' : score >= 70 ? '#FDE68A' : '#FECACA';

  return (
    <div
      className="rounded border bg-white"
      style={{ borderColor: score_border, background: score_bg }}
      data-testid="peppol-compliance-widget"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" style={{ color: score_kleur }} />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Peppol-compliance</div>
              <div className="text-sm font-bold text-gray-800">Wettelijk verplicht sinds 1/1/2026</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black leading-none" style={{ color: score_kleur }}>{score.toFixed(0)}%</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{data.periode}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div className="bg-white rounded p-2 border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Facturen</div>
            <div className="text-lg font-bold text-gray-800">{data.totaal_facturen}</div>
          </div>
          <div className="bg-white rounded p-2 border border-green-200">
            <div className="text-[10px] uppercase tracking-wider text-green-700">Via Peppol</div>
            <div className="text-lg font-bold text-[#059669]">{data.peppol_verzonden}</div>
          </div>
          <div className="bg-white rounded p-2 border" style={{ borderColor: data.te_versturen_count > 0 ? '#FECACA' : '#E5E7EB' }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: data.te_versturen_count > 0 ? '#B91C1C' : '#6B7280' }}>Te versturen</div>
            <div className="text-lg font-bold" style={{ color: data.te_versturen_count > 0 ? '#DC2626' : '#6B7280' }}>{data.te_versturen_count}</div>
          </div>
        </div>

        {data.te_versturen_count > 0 ? (
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-2.5">
            <div className="flex items-start gap-2">
              <Warning size={14} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-[11px] text-red-900">
                <strong>{data.te_versturen_count}</strong> B2B-factuur/facturen naar Peppol-klant(en) is/zijn nog niet via Peppol verzonden.
                Risico: boete €1.500–5.000 + BTW-aftrek geweigerd bij ontvanger.
              </div>
            </div>
            {data.te_versturen && data.te_versturen.length > 0 && (
              <div className="mt-2 max-h-28 overflow-y-auto">
                {data.te_versturen.slice(0, 5).map(f => (
                  <button
                    key={f.id}
                    onClick={() => navigate(`/documenten/${f.id}/bewerk`)}
                    className="w-full flex items-center justify-between text-[11px] py-1 px-1 hover:bg-red-100 transition-colors rounded"
                    data-testid={`peppol-todo-${f.id}`}
                  >
                    <span className="font-mono text-red-800">{f.id}</span>
                    <span className="text-red-700 truncate mx-2 flex-1 text-left">{f.klant}</span>
                    <span className="text-red-900 font-bold">{fmt(f.totaal)}</span>
                    <ArrowRight size={10} className="ml-1 text-red-500" />
                  </button>
                ))}
                {data.te_versturen.length > 5 && (
                  <div className="text-[10px] text-red-600 text-center mt-1">+{data.te_versturen.length - 5} meer…</div>
                )}
              </div>
            )}
            <button
              onClick={() => navigate('/facturen')}
              className="mt-2 w-full text-[11px] font-bold bg-red-600 text-white py-1.5 rounded hover:bg-red-700 transition-colors"
              data-testid="peppol-ga-naar-facturen"
            >
              Ga naar facturen — verstuur via Peppol
            </button>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-[#059669] bg-white rounded p-2 border border-green-200">
            <CheckCircle size={14} weight="fill" />
            <span>Alle B2B-facturen conform — goed gedaan! 🎉</span>
          </div>
        )}
      </div>
    </div>
  );
}
